import React, { useMemo, useCallback } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Spinner } from 'react-bootstrap'
import { useLocalReturnsState } from '../../stores/LocalReturnsProvider'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import TableProvider, { useTableState } from '../Table/TableProvider'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { t } from 'i18next'
import { columns } from './filterConfig.js'
import styled from 'styled-components'
import StructureIcon from '../Icons/StructureIcon'
import OverflowTooltip from '../OverflowTooltip.jsx'

const StyledStructureButton = styled.button`
  border: 0;
  padding: 0;
  background: none;
`
const defaultSorted = [
  {
    dataField: 'displayName',
    order: 'desc',
  },
]

const LocalReturnsTableInner = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    localReturns,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    selectedRow,
    setShowAllLocalReturns,
    setSorting,
    tableIsLoading,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
    setOpenStorehouseFilter,
    setShowOverlayLeft,
  } = useLocalReturnsState()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()
  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })
  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllLocalReturns(false)
      const { filters } = newState
      setFilter((prev) => ({
        ...prev,
        'filter.returnNo': filters['displayName']
          ? filters['displayName'].filterVal
          : '',
        'filter.orderNo': filters['order.orderNr']
          ? filters['order.orderNr'].filterVal
          : '',
        'filter.shippingOrderNo': filters['shippingOrder.ShippingOrderNumber']
          ? filters['shippingOrder.ShippingOrderNumber'].filterVal
          : '',
        'filter.customer': filters['customer.displayName']
          ? filters['customer.displayName'].filterVal
          : '',
        'filter.storehouseCoordinatesOrName': filters['storehouse.displayName']
          ? filters['storehouse.displayName'].filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '-' : ''}${sortField}`)
    }
    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }
  const handleStructureClick = useCallback(
    (dataField) => (e) => {
      e.stopPropagation()
      switch (dataField) {
        case 'storehouse.displayName':
          setOpenStorehouseFilter((prev) => !prev)
          setShowOverlayLeft(true)
          break
        default:
      }
    },
    [setOpenStorehouseFilter, setShowOverlayLeft]
  )
  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allCheckBoxesSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          case 'storehouse.displayName':
            return (
              <OverflowTooltip text={column.text}>
                {column.text}
                {components.sortElement}
                <div className='d-flex align-items-end'>
                  {components.filterElement}
                  <div className='ps-2'>
                    <StyledStructureButton
                      onClick={handleStructureClick(tmpColumn.dataField)}
                    >
                      <StructureIcon />
                    </StyledStructureButton>
                  </div>
                </div>
              </OverflowTooltip>
            )
          default:
            return (
              <OverflowTooltip text={column.text}>
                {column.text}
                {components.sortElement}
                <div className='d-flex align-items-end'>
                  {components.filterElement}
                </div>
              </OverflowTooltip>
            )
        }
      }
    })
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })
    const ignoreColumns = () => {
      var result = []

      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')
      return result
    }
    return tmpColumns.filter(
      (item) => !ignoreColumns().includes(item.dataField)
    )
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    handleStructureClick,
    noActions,
    withCheckbox,
  ])

  const getChosenFilters = () => {
    var message = []
    //save filters in Array
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('returnsTable:alertMsg')
    //returns Alert if no results for used filters
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      return (
        <div className='no-data-placeholder-alert'>
          <Alert variant='info'>{alertMessage}</Alert>
        </div>
      )
    }
    //returns empty Table
    else {
      return (
        <div className='no-data-placeholder'>
          {t('returnsTable:pleaseSearchLocalReturns')}
        </div>
      )
    }
  }
  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={localReturns}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const LocalReturnsTable = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useLocalReturnsState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <LocalReturnsTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default LocalReturnsTable
